import React, { useState, useEffect } from "react";

interface Gate {
    id: string;
    name: string;
    type: 'mpesa' | 'card' | 'bank' | 'airtel' | 'equitel';
    status: 'active' | 'inactive' | 'maintenance';
    balance: number;
    currency: string;
    dailyLimit: number;
    monthlyLimit: number;
    transactionCount: number;
    lastTransaction?: string;
    fees: {
        percentage: number;
        fixed: number;
    };
}

interface BillingGatesProps {
    gates: Gate[];
    userEmail: string;
}

const BillingGates: React.FC<BillingGatesProps> = ({ gates, userEmail }) => {
    const [selectedGate, setSelectedGate] = useState<Gate | null>(null);
    const [showGateModal, setShowGateModal] = useState(false);
    const [gateBalances, setGateBalances] = useState<{[key: string]: number}>({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchGateBalances();
    }, [gates]);

    const fetchGateBalances = async () => {
        setLoading(true);
        try {
            const balances: {[key: string]: number} = {};
            
            for (const gate of gates) {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://64.227.174.0:8004'}/billing/api/gate-balance/`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            user_email: userEmail,
                            gate_id: gate.id
                        })
                    });

                    const data = await response.json();
                    if (data.status === 'success') {
                        balances[gate.id] = data.data.balance;
                    } else {
                        balances[gate.id] = gate.balance;
                    }
                } catch (error) {
                    balances[gate.id] = gate.balance;
                }
            }
            
            setGateBalances(balances);
        } catch (error) {
            console.error('Failed to fetch gate balances:', error);
        } finally {
            setLoading(false);
        }
    };

    const getGateIcon = (type: string) => {
        switch (type) {
            case 'mpesa': return 'ri-smartphone-line text-green-600';
            case 'card': return 'ri-bank-card-line text-blue-600';
            case 'bank': return 'ri-bank-line text-purple-600';
            case 'airtel': return 'ri-signal-tower-line text-red-600';
            case 'equitel': return 'ri-phone-line text-orange-600';
            default: return 'ri-money-dollar-circle-line text-gray-600';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active': return 'success';
            case 'inactive': return 'danger';
            case 'maintenance': return 'warning';
            default: return 'secondary';
        }
    };

    const formatCurrency = (amount: number, currency: string = 'KES') => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const getGateStats = () => {
        const total = gates.length;
        const active = gates.filter(g => g.status === 'active').length;
        const totalBalance = Object.values(gateBalances).reduce((sum, balance) => sum + balance, 0);
        const totalTransactions = gates.reduce((sum, gate) => sum + gate.transactionCount, 0);

        return { total, active, totalBalance, totalTransactions };
    };

    const stats = getGateStats();

    const handleGateClick = (gate: Gate) => {
        setSelectedGate(gate);
        setShowGateModal(true);
    };

    return (
        <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 group">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-blue-50 rounded-xl group-hover:bg-blue-100 transition-colors">
                            <i className="ri-gate-line text-blue-600 text-xl"></i>
                        </div>
                        <div className="text-right">
                            <p className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full">Total</p>
                        </div>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-gray-900 mb-1">{stats.total}</p>
                        <p className="text-sm text-gray-600">Payment Gates</p>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 group">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-green-50 rounded-xl group-hover:bg-green-100 transition-colors">
                            <i className="ri-check-line text-green-600 text-xl"></i>
                        </div>
                        <div className="text-right">
                            <p className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">Active</p>
                        </div>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-green-600 mb-1">{stats.active}</p>
                        <p className="text-sm text-gray-600">Available Gates</p>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 group">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-purple-50 rounded-xl group-hover:bg-purple-100 transition-colors">
                            <i className="ri-wallet-3-line text-purple-600 text-xl"></i>
                        </div>
                        <div className="text-right">
                            <p className="text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded-full">Balance</p>
                        </div>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-gray-900 mb-1">
                            {formatCurrency(stats.totalBalance)}
                        </p>
                        <p className="text-sm text-gray-600">Total Balance</p>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 group">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-orange-50 rounded-xl group-hover:bg-orange-100 transition-colors">
                            <i className="ri-exchange-line text-orange-600 text-xl"></i>
                        </div>
                        <div className="text-right">
                            <p className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded-full">Volume</p>
                        </div>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-gray-900 mb-1">{stats.totalTransactions}</p>
                        <p className="text-sm text-gray-600">Transactions</p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Payment Gates</h3>
                        <p className="text-gray-600">Manage your payment gateways and view balances</p>
                    </div>
                    <button
                        onClick={fetchGateBalances}
                        disabled={loading}
                        className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold shadow-lg hover:shadow-xl"
                    >
                        {loading ? (
                            <div className="flex items-center">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                Refreshing...
                            </div>
                        ) : (
                            <div className="flex items-center">
                                <i className="ri-refresh-line mr-2"></i>
                                Refresh Balances
                            </div>
                        )}
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {gates.map((gate) => (
                        <div
                            key={gate.id}
                            onClick={() => handleGateClick(gate)}
                            className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-200 hover:border-gray-300 group hover:scale-105"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center">
                                    <div className={`p-4 rounded-2xl mr-4 ${
                                        gate.type === 'mpesa' ? 'bg-green-100' :
                                        gate.type === 'card' ? 'bg-blue-100' :
                                        gate.type === 'bank' ? 'bg-purple-100' :
                                        gate.type === 'airtel' ? 'bg-red-100' :
                                        'bg-orange-100'
                                    }`}>
                                        <i className={`ri ${getGateIcon(gate.type)} text-2xl ${
                                            gate.type === 'mpesa' ? 'text-green-600' :
                                            gate.type === 'card' ? 'text-blue-600' :
                                            gate.type === 'bank' ? 'text-purple-600' :
                                            gate.type === 'airtel' ? 'text-red-600' :
                                            'text-orange-600'
                                        }`}></i>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-900 text-lg">{gate.name}</h4>
                                        <p className="text-sm text-gray-600 capitalize">{gate.type}</p>
                                    </div>
                                </div>
                                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                                    gate.status === 'active' ? 'bg-green-100 text-green-800' :
                                    gate.status === 'inactive' ? 'bg-red-100 text-red-800' :
                                    'bg-yellow-100 text-yellow-800'
                                }`}>
                                    {gate.status}
                                </span>
                            </div>

                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Balance:</span>
                                    <span className="font-bold text-gray-900 text-lg">
                                        {formatCurrency(gateBalances[gate.id] || gate.balance, gate.currency)}
                                    </span>
                                </div>

                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Daily Limit:</span>
                                    <span className="font-semibold text-gray-900">
                                        {formatCurrency(gate.dailyLimit, gate.currency)}
                                    </span>
                                </div>

                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Monthly Limit:</span>
                                    <span className="font-semibold text-gray-900">
                                        {formatCurrency(gate.monthlyLimit, gate.currency)}
                                    </span>
                                </div>

                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Transactions:</span>
                                    <span className="font-semibold text-gray-900">{gate.transactionCount}</span>
                                </div>

                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Fees:</span>
                                    <span className="font-semibold text-gray-900">
                                        {gate.fees.percentage}% + {formatCurrency(gate.fees.fixed, gate.currency)}
                                    </span>
                                </div>
                            </div>

                            <div className="mt-6 pt-4 border-t border-gray-200">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500">Click to view details</span>
                                    <i className="ri-arrow-right-line text-gray-400 group-hover:text-gray-600 transition-colors"></i>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {showGateModal && selectedGate && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4 shadow-2xl">
                        <div className="flex items-center justify-between mb-8">
                            <h3 className="text-2xl font-bold text-gray-900">Gate Details</h3>
                            <button
                                onClick={() => setShowGateModal(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <i className="ri-close-line text-2xl"></i>
                            </button>
                        </div>

                        <div className="space-y-8">
                            <div className="flex items-center space-x-6">
                                <div className={`p-6 rounded-2xl ${
                                    selectedGate.type === 'mpesa' ? 'bg-green-100' :
                                    selectedGate.type === 'card' ? 'bg-blue-100' :
                                    selectedGate.type === 'bank' ? 'bg-purple-100' :
                                    selectedGate.type === 'airtel' ? 'bg-red-100' :
                                    'bg-orange-100'
                                }`}>
                                    <i className={`ri ${getGateIcon(selectedGate.type)} text-4xl ${
                                        selectedGate.type === 'mpesa' ? 'text-green-600' :
                                        selectedGate.type === 'card' ? 'text-blue-600' :
                                        selectedGate.type === 'bank' ? 'text-purple-600' :
                                        selectedGate.type === 'airtel' ? 'text-red-600' :
                                        'text-orange-600'
                                    }`}></i>
                                </div>
                                <div>
                                    <h4 className="text-2xl font-bold text-gray-900 mb-2">{selectedGate.name}</h4>
                                    <p className="text-gray-600 capitalize text-lg">{selectedGate.type} payment gate</p>
                                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full mt-3 ${
                                        selectedGate.status === 'active' ? 'bg-green-100 text-green-800' :
                                        selectedGate.status === 'inactive' ? 'bg-red-100 text-red-800' :
                                        'bg-yellow-100 text-yellow-800'
                                    }`}>
                                        {selectedGate.status}
                                    </span>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="bg-blue-50 rounded-2xl p-6">
                                    <p className="text-sm font-medium text-blue-600 mb-2">Current Balance</p>
                                    <p className="text-3xl font-bold text-blue-900">
                                        {formatCurrency(gateBalances[selectedGate.id] || selectedGate.balance, selectedGate.currency)}
                                    </p>
                                </div>
                                <div className="bg-green-50 rounded-2xl p-6">
                                    <p className="text-sm font-medium text-green-600 mb-2">Daily Limit</p>
                                    <p className="text-3xl font-bold text-green-900">
                                        {formatCurrency(selectedGate.dailyLimit, selectedGate.currency)}
                                    </p>
                                </div>
                                <div className="bg-purple-50 rounded-2xl p-6">
                                    <p className="text-sm font-medium text-purple-600 mb-2">Monthly Limit</p>
                                    <p className="text-3xl font-bold text-purple-900">
                                        {formatCurrency(selectedGate.monthlyLimit, selectedGate.currency)}
                                    </p>
                                </div>
                            </div>

                            <div className="bg-gray-50 rounded-2xl p-6">
                                <h5 className="font-bold text-gray-900 mb-4 text-lg">Transaction Statistics</h5>
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Total Transactions</p>
                                        <p className="text-2xl font-bold text-gray-900">{selectedGate.transactionCount}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Transaction Fees</p>
                                        <p className="text-2xl font-bold text-gray-900">
                                            {selectedGate.fees.percentage}% + {formatCurrency(selectedGate.fees.fixed, selectedGate.currency)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex space-x-4">
                                <button className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-semibold">
                                    <i className="ri-eye-line mr-2"></i>
                                    View Transactions
                                </button>
                                <button className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-semibold">
                                    <i className="ri-settings-3-line mr-2"></i>
                                    Configure Gate
                                </button>
                                <button className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-semibold">
                                    <i className="ri-wifi-line mr-2"></i>
                                    Test Connection
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BillingGates;
